import { useState } from 'react';
import { Button } from '@safetyhub/ui';
import Modal from './Modal';
import { extractApiError } from '../lib/api';

interface RestoreConfirmModalProps {
  isOpen: boolean;
  daysLeft: number;
  restoreToken: string;
  onRestore: (restoreToken: string) => Promise<void>;
  onCancel: () => void;
}

export default function RestoreConfirmModal({
  isOpen,
  daysLeft,
  restoreToken,
  onRestore,
  onCancel,
}: RestoreConfirmModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRestore = async () => {
    setLoading(true);
    setError(null);
    try {
      await onRestore(restoreToken);
      // 성공 시 부모(Login)가 navigate로 처리
    } catch (err) {
      // AxiosError도 Error라 `err.message`는 "Request failed with status code 410"이 된다.
      // 서버가 준 한국어 사유를 쓴다(restore.tsx와 같은 축).
      setError(extractApiError(err).message || '계정 복원에 실패했습니다.');
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (loading) return;
    onCancel();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="계정 복원"
      size="sm"
      preventClose={loading}
      footer={
        <>
          <Button variant="secondary" onClick={handleCancel} disabled={loading}>
            취소
          </Button>
          <Button variant="primary" onClick={handleRestore} disabled={loading}>
            {loading ? '복원 중...' : '복원하기'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* 안내 아이콘 */}
        <div className="flex justify-center">
          <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-yellow-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z"
              />
            </svg>
          </div>
        </div>

        {/* 안내 문구 */}
        <div className="text-center space-y-1">
          <p className="text-sm text-ink-700">완전 삭제 대기 중인 계정입니다.</p>
          <p className="text-sm font-medium text-yellow-700">
            ({daysLeft}일 남음)
          </p>
          <p className="text-sm text-ink-700 mt-2">복원하시겠습니까?</p>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <p className="text-xs text-red-600 text-center">{error}</p>
        )}
      </div>
    </Modal>
  );
}
